import { useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { View, ViewType } from 'api/views/types';
import {
  CreatableView,
  getViewTranformer,
  Views,
} from 'components/molecules/views';
import { empty, loading } from 'components/molecules/views/core';
import { useViewActions } from 'components/molecules/views/view-actions';
import { FilterableField } from 'components/organisms/tasks/fields';
import {
  getTimesheetReportsViewsQuery,
  useTimesheetReportViewsQuery,
} from 'queries/timesheets/report';
import { getViewDefinitionTransformer } from './definition-transformer';
import { TimesheetReportViewDefinition } from './types';

interface Props {
  fieldLookup: Record<string, FilterableField>;
  isFieldsLoading: boolean;
}
interface Result<T = TimesheetReportViewDefinition> {
  views: Views<T>;
  onViewCreate: (view: CreatableView<T>) => Promise<View<T>>;
  onViewUpdate: (view: View<T>) => Promise<View<T>>;
  onViewDelete: (view: View<T>) => Promise<View<T>>;
}

export const useTimesheetOverallReportViews = ({
  fieldLookup,
  isFieldsLoading,
}: Props): Result => {
  const viewDefinitionTransformer = useMemo(
    () => getViewDefinitionTransformer(fieldLookup),
    [fieldLookup],
  );
  const viewTransformer = useMemo(
    () => getViewTranformer(viewDefinitionTransformer),
    [viewDefinitionTransformer],
  );
  const { data, isLoading } = useTimesheetReportViewsQuery(viewTransformer, {
    enabled: !isFieldsLoading,
  });
  const views = useMemo(
    () =>
      isLoading ? loading<TimesheetReportViewDefinition>() : data ?? empty,
    [data, isLoading],
  );
  const queryClient = useQueryClient();
  const { onViewCreate, onViewUpdate, onViewDelete } = useViewActions({
    views,
    setViews: (newViews) =>
      queryClient.setQueryData(getTimesheetReportsViewsQuery(), newViews),
    viewType: ViewType.TimesheetReports,
    transformer: viewDefinitionTransformer,
  });

  return {
    onViewCreate,
    onViewUpdate,
    onViewDelete,
    views,
  };
};
